import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { UncontrolledTooltip } from 'reactstrap';

import { LOAD_MORE_PAGE_SIZE } from '~constants/common';

import { getErrorMessage } from '~libs/axiosWrapper';
import { useToast } from '~libs/toast';
import { CareRecipientProblem, readCareRecipientProblem } from '~support-at-home/lib/careRecipientErrors';

import { ClickableIcon, ErrorResponse, Toolbar } from '~components';
import Switch from '~components/form-fields/inputs/Switch';
import { faRefresh } from '~components/icon';
import { SortState, TableView } from '~components/table-view';

import { CareRecipient, CareRecipientListQuery } from '~support-at-home/models/careRecipients';

import { CareRecipientRow, columnsFactory } from '~support-at-home/forms/care-recipients/list';

import { setTitle } from '~main/actions/login';
import {
  useGetCareRecipientsQuery,
  useIgnoreCareRecipientMutation,
  useLinkCareRecipientMutation,
  useRestoreCareRecipientMutation,
  useSyncCareRecipientsMutation,
  useUnlinkCareRecipientMutation,
} from '~support-at-home/actions/careRecipients';

import CareRecipientLinkModal from './CareRecipientLinkModal';

type CareRecipientSortBy = NonNullable<CareRecipientListQuery['sortBy']>;
const SORTABLE_COLUMNS = new Set<CareRecipientSortBy>(['care_recipient_id', 'name', 'birth_date']);

const IN_CARE_UNAVAILABLE_NOTE = 'Add the NAPS ID to the PRODA connection to filter by in care';

interface ComponentProps {
  isActive?: boolean;
}

const nameOf = (careRecipient: CareRecipient) => careRecipient.name || careRecipient.care_recipient_id;

const CareRecipients: React.FC<ComponentProps> = ({ isActive = true }) => {
  const [showIgnored, setShowIgnored] = useState<boolean>(false);
  // false = "In care now" (the default), true = every care recipient Services Australia holds.
  const [showAll, setShowAll] = useState<boolean>(false);
  const [inCareAvailable, setInCareAvailable] = useState<boolean>(true);
  const [limit, setLimit] = useState<number>(LOAD_MORE_PAGE_SIZE);
  const [sortState, setSortState] = useState<SortState>([]);
  const [linkTarget, setLinkTarget] = useState<CareRecipient | null>(null);
  // Set when the last sync failed and cleared by the next one that works, so the list is never read as
  // current while it may be out of date.
  const [syncProblem, setSyncProblem] = useState<string | null>(null);
  const hasSynced = useRef<boolean>(false);
  const { showToast } = useToast();

  useEffect(() => {
    if (isActive) setTitle('Support at Home - Care Recipients');
  }, [isActive]);

  // Reset paging whenever the view changes so users see a fresh first page.
  useEffect(() => {
    setLimit(LOAD_MORE_PAGE_SIZE);
  }, [showIgnored, showAll]);

  const queryArgs = useMemo(() => {
    const base = { is_ignored: showIgnored, in_care: !showAll, limit };
    const active = sortState.find(({ direction }) => direction !== null);
    if (active && SORTABLE_COLUMNS.has(active.id as CareRecipientSortBy)) {
      return {
        ...base,
        sortBy: active.id as CareRecipientSortBy,
        sortOrder: active.direction === 'asc' ? ('asc' as const) : ('desc' as const),
      };
    }
    return base;
  }, [showIgnored, showAll, limit, sortState]);

  const { data, isFetching, isError, error } = useGetCareRecipientsQuery(queryArgs, {
    skip: !isActive,
    refetchOnMountOrArgChange: true,
  });

  const [syncCareRecipients, syncState] = useSyncCareRecipientsMutation();
  const [linkCareRecipient, linkState] = useLinkCareRecipientMutation();
  const [unlinkCareRecipient, unlinkState] = useUnlinkCareRecipientMutation();
  const [ignoreCareRecipient, ignoreState] = useIgnoreCareRecipientMutation();
  const [restoreCareRecipient, restoreState] = useRestoreCareRecipientMutation();
  const isBusy = linkState.isLoading || unlinkState.isLoading || ignoreState.isLoading || restoreState.isLoading;
  const isSyncing = syncState.isLoading;

  // Pulls the latest care recipients from Services Australia into TurnPoint. The list only reads what was
  // last pulled, so this runs once when the tab is first opened and again on Refresh.
  const runSync = useCallback(async () => {
    try {
      const result = await syncCareRecipients().unwrap();
      setSyncProblem(null);
      setInCareAvailable(result.in_care_available);
      if (!result.in_care_available) setShowAll(true);
      if (result.truncated) {
        showToast(
          'info',
          'Services Australia returned more care recipients than could be loaded. The list is partial.',
        );
      }
    } catch (err) {
      const { message } = readCareRecipientProblem(err);
      setSyncProblem(message);
      showToast('danger', message);
    }
  }, [syncCareRecipients, showToast]);

  useEffect(() => {
    if (!isActive || hasSynced.current) return;
    hasSynced.current = true;
    runSync();
  }, [isActive, runSync]);

  // `data.rows` already satisfies CareRecipientRow; one array-level cast keeps the reference stable.
  const rows = useMemo<CareRecipientRow[]>(() => (data?.rows ?? []) as CareRecipientRow[], [data?.rows]);
  const total = data?.total ?? 0;

  // The count belongs to the view that matches `data.rows`; while a fetch is in flight it is swapped
  // for an ellipsis so the label never shows the previous view's number.
  const totalDisplay = isFetching || isSyncing ? '…' : total;
  const toggleLabels = useMemo<[string, string]>(
    () => [`${totalDisplay} active`, `${totalDisplay} ignored`],
    [totalDisplay],
  );
  const inCareLabels: [string, string] = ['In care now', 'All care recipients'];

  const handleLoadMore = useCallback(() => {
    setLimit((current) => current + LOAD_MORE_PAGE_SIZE);
  }, []);

  const handleSort = useCallback((next: SortState) => {
    setSortState(next);
    setLimit(LOAD_MORE_PAGE_SIZE);
  }, []);

  const handleIgnore = useCallback(
    async (careRecipient: CareRecipient) => {
      try {
        await ignoreCareRecipient({ care_recipient_id: careRecipient.care_recipient_id }).unwrap();
        showToast('success', `Ignored "${nameOf(careRecipient)}"`);
      } catch (err) {
        showToast('danger', readCareRecipientProblem(err).message);
      }
    },
    [ignoreCareRecipient, showToast],
  );

  const handleRestore = useCallback(
    async (careRecipient: CareRecipient) => {
      try {
        await restoreCareRecipient({ care_recipient_id: careRecipient.care_recipient_id }).unwrap();
        showToast('success', `Restored "${nameOf(careRecipient)}"`);
      } catch (err) {
        showToast('danger', readCareRecipientProblem(err).message);
      }
    },
    [restoreCareRecipient, showToast],
  );

  const handleUnlink = useCallback(
    async (careRecipient: CareRecipient) => {
      try {
        await unlinkCareRecipient({ care_recipient_id: careRecipient.care_recipient_id }).unwrap();
        showToast('success', `Unlinked "${nameOf(careRecipient)}"`);
      } catch (err) {
        showToast('danger', readCareRecipientProblem(err).message);
      }
    },
    [unlinkCareRecipient, showToast],
  );

  // Returns null when linked (the window closes) or the problem so the window can show it. A 409
  // `confirm_overwrite` is not an error to the user: the window turns it into the confirm step.
  const handleLinkSubmit = useCallback(
    async (clientId: number, confirmOverwrite: boolean): Promise<CareRecipientProblem | null> => {
      if (!linkTarget) return null;
      try {
        const result = await linkCareRecipient({
          care_recipient_id: linkTarget.care_recipient_id,
          client_id: clientId,
          confirm_overwrite: confirmOverwrite,
        }).unwrap();
        showToast(
          'success',
          result.changed
            ? `Linked "${nameOf(linkTarget)}" to ${result.client_name ?? 'the client'}`
            : `"${nameOf(linkTarget)}" was already linked to ${result.client_name ?? 'this client'}`,
        );
        setLinkTarget(null);
        return null;
      } catch (err) {
        return readCareRecipientProblem(err);
      }
    },
    [linkCareRecipient, linkTarget, showToast],
  );

  const columns = useMemo(
    () =>
      columnsFactory({
        onLink: setLinkTarget,
        onIgnore: handleIgnore,
        onRestore: handleRestore,
        onUnlink: handleUnlink,
        isBusy: isBusy || isSyncing,
      }),
    [handleIgnore, handleRestore, handleUnlink, isBusy, isSyncing],
  );

  return (
    <div className="p-2 sah-care-recipients">
      <Toolbar
        id="sah-care-recipients"
        leftCluster={
          <div className="d-flex align-items-center gap-3">
            <div className="d-flex align-items-center gap-2">
              <Switch
                id="sah-care-recipients-ignored-toggle"
                type="switch"
                name="sah-care-recipients-ignored-toggle"
                value={showIgnored}
                onChange={(_id, val) => setShowIgnored(val)}
                labels={toggleLabels}
              />
              <UncontrolledTooltip target="sah-care-recipients-ignored-toggle" placement="top">
                {showIgnored ? 'Show active care recipients' : 'Show ignored care recipients'}
              </UncontrolledTooltip>
            </div>
            <span
              className="d-flex align-items-center gap-2"
              title={inCareAvailable ? undefined : IN_CARE_UNAVAILABLE_NOTE}
              data-cy="sah-care-recipients-in-care-wrap"
            >
              <Switch
                id="sah-care-recipients-in-care-toggle"
                type="switch"
                name="sah-care-recipients-in-care-toggle"
                value={showAll}
                disabled={!inCareAvailable}
                onChange={(_id, val) => setShowAll(val)}
                labels={inCareLabels}
              />
            </span>
          </div>
        }
        rightCluster={
          <ClickableIcon
            className="me-2"
            icon={faRefresh}
            id="sah-care-recipients-refresh-button"
            onClick={() => runSync()}
            disabled={isSyncing || isFetching}
            toolTip="Refresh from Services Australia"
            ariaLabel="Refresh care recipients"
            dataCy="sah-care-recipients-refresh-button"
          />
        }
      />
      {syncProblem && (
        <div className="alert alert-warning mb-2 py-2" role="alert" data-cy="sah-care-recipients-sync-problem">
          Couldn&apos;t refresh from Services Australia ({syncProblem}). This list may be out of date. Use Refresh to
          try again.
        </div>
      )}
      {isError ? (
        <ErrorResponse message={getErrorMessage(error)} />
      ) : (
        <div className="sah-care-recipients-scroll" aria-busy={isFetching || isSyncing}>
          <TableView
            id="sah-care-recipients-table"
            columns={columns}
            data={rows}
            isLoading={isFetching || isSyncing}
            emptyMessage={showIgnored ? 'No ignored care recipients' : 'No care recipients'}
            readonly
            striped
            allowPagination
            totalCount={total}
            onLoadMore={handleLoadMore}
            allowSort
            initialSortState={sortState}
            onSort={handleSort}
            showCount
            pageSize={limit}
          />
        </div>
      )}
      <CareRecipientLinkModal
        careRecipient={linkTarget}
        isOpen={linkTarget !== null}
        isSubmitting={linkState.isLoading}
        onCancel={() => setLinkTarget(null)}
        onSubmit={handleLinkSubmit}
      />
    </div>
  );
};

export default CareRecipients;
